import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { DateRange } from '@/components/ui/filters/DateRangeFilterChip'
import { currentMonthRange } from '@/hooks/useOperationsDashboard'

// One shared Período for every operations/admin view. Lives at OperationsLayout
// so it survives page-to-page navigation (only the <Outlet/> child remounts).
// Every page reads the SAME value — there are no per-page defaults — so the
// period is identical across views and persists (localStorage) across reloads.
// Default: the current month.

const STORAGE_KEY = 'jca.period'

function loadPeriod(): DateRange {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as { from?: string; to?: string }
      if (parsed.from && parsed.to) {
        const from = new Date(parsed.from)
        const to = new Date(parsed.to)
        if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) return { from, to }
      }
    }
  } catch {
    /* malformed / unavailable — fall through to default */
  }
  return currentMonthRange()
}

function savePeriod(range: DateRange): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ from: range.from.toISOString(), to: range.to.toISOString() }),
    )
  } catch {
    /* localStorage unavailable / quota — non-fatal, period stays in memory */
  }
}

type PeriodContextValue = {
  period: DateRange
  setPeriod: (next: DateRange) => void
}

const PeriodContext = createContext<PeriodContextValue | null>(null)

export function PeriodProvider({ children }: { children: ReactNode }) {
  const [period, setPeriodState] = useState<DateRange>(() => loadPeriod())
  const setPeriod = useCallback((next: DateRange) => {
    setPeriodState(next)
    savePeriod(next)
  }, [])
  const value = useMemo(() => ({ period, setPeriod }), [period, setPeriod])
  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>
}

export function usePeriod(): PeriodContextValue {
  const ctx = useContext(PeriodContext)
  // Fallback to component-local state when rendered outside a PeriodProvider
  // (e.g. unit tests that mount a page in isolation). In the app every period
  // consumer lives under OperationsLayout's provider, so this never shares.
  const [localPeriod, setLocalPeriod] = useState<DateRange>(() => currentMonthRange())
  const local = useMemo<PeriodContextValue>(
    () => ({ period: localPeriod, setPeriod: setLocalPeriod }),
    [localPeriod],
  )
  return ctx ?? local
}
